import useAuth from "src/hooks/useAuth";
import { createContext, ReactNode } from "react";
import { Application, User, UserForm } from "src/@type/management/user";
import { mapperApllicationDTOToModel, mapperUserDTOToModel } from "./DTO/DTOToModel";
import { ApplicationDTO, UserDTO } from "./DTO/DTO";

type ManagementDataContextType = {
    getApplications: () => Promise<Application[]>
    getUsers: () => Promise<User[]>
    insertUser: (user: UserForm | null) => Promise<User>
    updateUser: (user: User) => Promise<User>
    updateUserPassword: (userID: number, password: string) => Promise<boolean>
};

export const ManagementDataContext = createContext<ManagementDataContextType | null>(null);

type ManagementDataContextProviderProps = {
    children: ReactNode;
};

export function ManagementDataContextProvider({ children }: ManagementDataContextProviderProps) {
    const { axios } = useAuth();

    const insertUser = (user: UserForm | null) => new Promise<User>((resolve, reject) => {
        axios.put('/mid/management/user/', user).then(response => {
            const { data, error } = response.data;
            if (error) reject(error);
            else resolve(mapperUserDTOToModel(data as UserDTO));
        }).catch(err => {
            reject(err.message);
        });
    });

    const getApplications = () => new Promise<Application[]>((resolve, reject) => {
        axios.get('/mid/management/applications/')
            .then(response => {
                const { data, error } = response.data;

                if (error) reject(error);
                else resolve((data as ApplicationDTO[]).map(mapperApllicationDTOToModel));
            }).catch(err => {
                reject(err.message);
            });
    });

    const getUsers = () => new Promise<User[]>((resolve, reject) => {
        axios.get('/mid/management/users/')
            .then(response => {
                const { data, error } = response.data;
                if (error) reject(error);
                else resolve((data as UserDTO[]).map(mapperUserDTOToModel));
            }).catch(err => {
                reject(err.message);
            });
    });

    const updateUser = (user: User) => new Promise<User>((resolve, reject) => {
        axios.post('/mid/management/user', user)
            .then(response => {
                const { data, error } = response.data;
                if (error) reject(error);
                else resolve(mapperUserDTOToModel(data as UserDTO));
            }).catch(err => {
                reject(err.message);
            });
    });


    const updateUserPassword = (userID: number, password: string) => new Promise<boolean>((resolve, reject) => {
        axios.post('/mid/management/user/password', { userID: userID, password: password })
            .then(response => {
                const { data, error } = response.data;
                if (error) reject(error);
                else resolve(true);
            }).catch(err => {
                reject(err.message);
            });
    });

    return (
        <ManagementDataContext.Provider
            value={{
                getApplications,
                getUsers,
                insertUser,
                updateUser,
                updateUserPassword,
            }}
        >
            {children}
        </ManagementDataContext.Provider>
    );
}
